import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message, Dropdown } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getSocialAccounts,
  deleteSocialAccount,
  connectSocialAccount,
  disconnectSocialAccount,
  refreshSocialAccountToken,
  type SocialAccount,
  type SocialAccountFilters,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  expired: 'red',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

export default function SocialAccountListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<SocialAccountFilters>({});

  const canWrite = hasPermission('social_accounts', 'write');
  const canDelete = hasPermission('social_accounts', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof SocialAccountFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getSocialAccounts({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('social.action.delete_confirm_account'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteSocialAccount(id);
            message.success(t('social.delete_account_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleLifecycle = useCallback(
    async (action: 'connect' | 'disconnect' | 'refresh_token', id: string) => {
      try {
        if (action === 'connect') await connectSocialAccount(id);
        else if (action === 'disconnect') await disconnectSocialAccount(id);
        else await refreshSocialAccountToken(id);
        message.success(t(`social.action.${action}_success`));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<SocialAccount> = useMemo(
    () => [
      {
        title: t('social.field.account_name'),
        dataIndex: 'account_name',
        key: 'account_name',
        sorter: true,
        ellipsis: true,
        render: (name: string, record: SocialAccount) => (
          <a onClick={() => navigate(`/social/accounts/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('social.field.platform'),
        dataIndex: 'platform',
        key: 'platform',
        width: 120,
        render: (platform: string) => (
          <Tag color={PLATFORM_COLORS[platform] ?? 'default'}>
            {t(`social.platform.${platform}`, platform)}
          </Tag>
        ),
      },
      {
        title: t('social.field.account_id'),
        dataIndex: 'account_id',
        key: 'account_id',
        ellipsis: true,
        width: 140,
      },
      {
        title: t('social.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`social.status.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('social.field.follower_count'),
        dataIndex: 'follower_count',
        key: 'follower_count',
        width: 100,
        sorter: true,
        render: (val: number) => val?.toLocaleString() ?? '0',
      },
      {
        title: t('social.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 200,
        render: (_: unknown, record: SocialAccount) => {
          const lifecycleItems = [];
          if (record.status !== 'active' && canWrite) {
            lifecycleItems.push({
              key: 'connect',
              icon: <LinkOutlined />,
              label: t('social.action.connect'),
              onClick: () => handleLifecycle('connect', record.id),
            });
          }
          if (record.status === 'active' && canWrite) {
            lifecycleItems.push({
              key: 'disconnect',
              icon: <DisconnectOutlined />,
              label: t('social.action.disconnect'),
              onClick: () => handleLifecycle('disconnect', record.id),
            });
          }
          if (canWrite) {
            lifecycleItems.push({
              key: 'refresh_token',
              icon: <ReloadOutlined />,
              label: t('social.action.refresh_token'),
              onClick: () => handleLifecycle('refresh_token', record.id),
            });
          }

          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/social/accounts/${record.id}`)}
              />
              {canWrite && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/social/accounts/${record.id}/edit`)}
                />
              )}
              {canDelete && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              )}
              {lifecycleItems.length > 0 && (
                <Dropdown menu={{ items: lifecycleItems }}>
                  <Button type="link" size="small" icon={<DownOutlined />} />
                </Dropdown>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleLifecycle]
  );

  const platformOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'facebook', label: t('social.platform.facebook') },
      { value: 'instagram', label: t('social.platform.instagram') },
      { value: 'twitter', label: t('social.platform.twitter') },
      { value: 'linkedin', label: t('social.platform.linkedin') },
      { value: 'wechat', label: t('social.platform.wechat') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('social.filter.all') },
      { value: 'active', label: t('social.status.active') },
      { value: 'inactive', label: t('social.status.inactive') },
      { value: 'expired', label: t('social.status.expired') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('social.filter.platform')}
        style={{ width: 140 }}
        allowClear
        options={platformOptions}
        onChange={(val) => handleFilterChange('platform', val || undefined)}
      />
      <Select
        placeholder={t('social.filter.status')}
        style={{ width: 120 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/social/accounts/new')}
        >
          {t('social.new_account')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('social.accounts_title')}</h2>
      <DataGrid<SocialAccount>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('social.search_accounts_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/social/accounts/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
